import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { BookOpen, Search, ArrowLeft, GraduationCap, FileText, Users, ChevronRight } from 'lucide-react';

const BrowseCourses = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
  }, [selectedSubject]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedSubject) params.subject = selectedSubject;
      if (searchQuery) params.search = searchQuery;
      const response = await axios.get(`${API}/courses`, { params });
      setCourses(response.data.courses || []);
      setSubjects(response.data.subjects || []);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchCourses();
  };

  const subjectColors = {
    'Chemistry': 'from-emerald-500 to-teal-600',
    'Computer Science': 'from-blue-500 to-indigo-600',
    'Biology': 'from-green-500 to-emerald-600',
    'Physics': 'from-violet-500 to-purple-600',
    'Mathematics': 'from-amber-500 to-orange-600',
    'Engineering': 'from-slate-500 to-gray-700',
    'Medicine': 'from-red-500 to-rose-600',
    'Business': 'from-cyan-500 to-blue-600',
    'Psychology': 'from-pink-500 to-fuchsia-600',
    'Literature': 'from-yellow-600 to-amber-700',
    'History': 'from-stone-500 to-stone-700',
  };

  const getGradient = (subject) => subjectColors[subject] || 'from-gray-500 to-gray-700';

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-blue-50 to-stone-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                data-testid="back-to-home-btn"
                onClick={() => navigate('/')}
                variant="ghost"
                size="icon"
                className="rounded-xl"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center shadow-md">
                  <BookOpen className="w-6 h-6 text-blue-50" />
                </div>
                <span className="text-xl md:text-2xl font-bold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>DocMarket</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 md:py-10">
        {/* Hero */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <GraduationCap className="w-8 h-8 text-blue-700" />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Crimson Text, serif' }}>
              Course Study Hubs
            </h1>
          </div>
          <p className="text-base text-gray-600 max-w-2xl">
            Browse study materials organized by course. Find notes, guides, and exam prep for your classes.
          </p>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-2xl shadow-md p-4 md:p-5 mb-8 border border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                data-testid="course-search-input"
                type="text"
                placeholder="Search courses by name, code, or subject..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="rounded-xl"
              />
            </div>
            <Button
              data-testid="course-search-btn"
              onClick={handleSearch}
              className="rounded-xl bg-blue-600 hover:bg-blue-700"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>

          {/* Subject Filter Pills */}
          {subjects.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                data-testid="subject-filter-all"
                onClick={() => setSelectedSubject('')}
                className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                  !selectedSubject
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Subjects
              </button>
              {subjects.map((subject) => (
                <button
                  key={subject}
                  data-testid={`subject-filter-${subject.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setSelectedSubject(selectedSubject === subject ? '' : subject)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                    selectedSubject === subject
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {subject}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Course Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : courses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {courses.map((course) => (
              <Link
                key={course.id}
                to={`/course/${course.slug}`}
                data-testid={`course-card-${course.slug}`}
                className="group bg-white rounded-2xl shadow-md hover:shadow-xl border border-gray-200 overflow-hidden transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`h-2 bg-gradient-to-r ${getGradient(course.subject)}`} />
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    {course.code && (
                      <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-mono font-semibold rounded-lg">
                        {course.code}
                      </span>
                    )}
                    <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg">
                      {course.subject}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-700 transition-colors line-clamp-2" style={{ fontFamily: 'Crimson Text, serif' }}>
                    {course.name}
                  </h3>
                  {course.school && (
                    <p className="text-sm text-gray-500 mb-3">{course.school}</p>
                  )}
                  <div className="flex items-center gap-4 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <FileText className="w-4 h-4" />
                      <span>{course.document_count} {course.document_count === 1 ? 'doc' : 'docs'}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Users className="w-4 h-4" />
                      <span>{course.seller_count} {course.seller_count === 1 ? 'contributor' : 'contributors'}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 ml-auto group-hover:text-blue-600 transition-colors" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">No courses found</p>
            <p className="text-gray-400 text-sm">
              Courses are created automatically when sellers upload documents.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default BrowseCourses;
